import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from "@angular/common/http";
import { APP_INITIALIZER, importProvidersFrom, provideZoneChangeDetection } from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { AuthModule, getAuth, provideAuth } from "@angular/fire/auth";
import { ReactiveFormsModule } from "@angular/forms";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { environment } from "@env";
import { Region } from "@fingerprintjs/fingerprintjs-pro";
import {
  CacheLocation,
  FingerprintjsProAngularModule,
} from "@fingerprintjs/fingerprintjs-pro-angular";
import {
  TRANSLOCO_LOADER,
  TranslocoService,
  provideTransloco,
  translocoConfig,
} from "@ngneat/transloco";
import { provideEffects } from "@ngrx/effects";
import { provideStore } from "@ngrx/store";
import {
  ChallengesEffects,
  CountriesEffects,
  UserEffects,
  challengesReducer,
  countriesReducer,
  userReducer,
} from "@store";
import { IntercomModule } from "@supy-io/ngx-intercom";
import { RECAPTCHA_SETTINGS, RecaptchaFormsModule, RecaptchaModule } from "ng-recaptcha";
import { ToastrModule } from "ngx-toastr";
import { register } from "swiper/element/bundle";
import { AppComponent } from "./app/app.component";
import { routes } from "./app/app.routes";
import { ApiInterceptor, ErrorInterceptor } from "./app/core/interceptor";
import { TRANSLOCO_CONFIG, TranslocoHttpLoader, loadTranslations } from "./app/transloco.config";
import { newChallengesReducer } from "./app/store/new-challenge-creation/new-challenge.reducer";
import { upgradeChallengesReducer } from "./app/store/upgrade-challenge-creation/upgrade-challenge.reducer";
import { futuresNewChallengesReducer } from "./app/store/futures-new-challenge/futures-new-challenge.reducer";

//test
function setAffiliateId(affiliateId: string) {
  //somehow sometimes a null string was sent from the marketing side but wasnt caught causing BE errors, now we specifically filter it out here
  if (affiliateId !== "null") {
    sessionStorage.setItem("affiliateId", affiliateId);
  }
}

function setCookie(name: string, value: string, days: number) {
  const maxAge = days * 24 * 60 * 60;
  const domain = "." + window.location.hostname.split(".").slice(-2).join("."); // example.com
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; domain=${domain}; max-age=${maxAge}; secure; samesite=Lax`;

  const url = new URL(window.location.href);
  url.searchParams.delete("click_id");
}

// Session cookie (no Expires / Max-Age), same domain resolution as setCookie.
function setSessionCookie(name: string, value: string): void {
  const domain: string = "." + window.location.hostname.split(".").slice(-2).join("."); // example.com
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; domain=${domain}; secure`;
}

export function handleReferralRedirect(): boolean {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const urlParams = new URLSearchParams(window.location.search);
  const affiliateIdFromQuery = urlParams.get("affiliateId");
  const clickId = urlParams.get("click_id");
  const fbc = urlParams.get("_fbc"); ///meta click id
  const gclid = urlParams.get("gclid"); //google click id
  const twclid = urlParams.get("twclid"); //x click id
  const ttclid = urlParams.get("ttclid"); //tiktok click id
  const queryParamsArr = [
    { label: "click_id", value: clickId, time: 30 },
    { label: "_fbc", value: fbc, time: 1 / 24 },
    { label: "gclid", value: gclid, time: 1 / 24 },
    { label: "twclid", value: twclid, time: 1 / 24 },
    { label: "ttclid", value: ttclid, time: 1 / 24 },
  ].filter((param): param is { label: string; value: string; time: number } => !!param.value);
  //this map fucntion was commented out on 10-03-2026 because the cookie name was the value not the label
  //.map((param) => ({ value: param.value, time: param.time }));
  const tracknowClickId: string | null = urlParams.get("tn_click_id"); //tracknow click id
  const tracknowRefId: string | null = urlParams.get("tn_ref_id"); //tracknow referral id
  const tracknowCampaignId: string | null = urlParams.get("tn_campaign_id"); //tracknow campaign id
  const sessionCookiesQueryParamsArr: { label: string; value: string }[] = [
    { label: "tn_click_id", value: tracknowClickId },
    { label: "tn_ref_id", value: tracknowRefId },
    { label: "tn_campaign_id", value: tracknowCampaignId },
  ].filter((param): param is { label: string; value: string } => !!param.value);

  const url = new URL(window.location.href);

  if (sessionCookiesQueryParamsArr.length) {
    sessionCookiesQueryParamsArr.forEach((param) => {
      setSessionCookie(param.label, param.value);
    });
  }

  if (queryParamsArr.length) {
    queryParamsArr.forEach((param) => {
      setCookie(param.label, param.value, param.time);
    });
  }

  if (affiliateIdFromQuery) {
    setAffiliateId(affiliateIdFromQuery);
    url.searchParams.delete("affiliateId");
    window.history.replaceState({}, "", url.toString());

    return true;
  }

  if (pathParts.length === 3 && ["en", "de", "fr"].includes(pathParts[0]) && pathParts[1] === "a") {
    setAffiliateId(pathParts[2]);
    window.location.replace(`/${pathParts[0]}`);
    return false;
  }

  if (pathParts.length === 2 && pathParts[0] === "a") {
    setAffiliateId(pathParts[1]);
    window.location.replace("/en");
    return false;
  }

  return true;
}

register();

if (handleReferralRedirect()) {
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        RecaptchaModule,
        RecaptchaFormsModule,
        BrowserModule,
        ReactiveFormsModule,
        RouterModule.forRoot(routes, { scrollPositionRestoration: "enabled" }),
        ToastrModule.forRoot(),
        IntercomModule.forRoot({ appId: environment.intercomAppId, updateOnRouterChange: true }),
        AuthModule,

        FingerprintjsProAngularModule.forRoot({
          loadOptions: {
            apiKey: environment.fpApiKey,
            region: environment.fpRegion as Region,
          },
          cacheLocation: CacheLocation.SessionStorage,
          cacheTimeInSeconds: environment.limitFingerprint ? 43200 * 2 : 43200,
        }),
      ),
      provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
      provideAuth(() => getAuth()),
      provideHttpClient(withXhr(), withInterceptorsFromDi()),
      provideStore({
        user: userReducer,
        challenges: challengesReducer,
        countries: countriesReducer,
        newChallenge: newChallengesReducer,
        upgradeChallenge: upgradeChallengesReducer,
        futuresNewChallenge: futuresNewChallengesReducer,
      }),
      provideAnimations(),
      provideZoneChangeDetection(),
      provideEffects(ChallengesEffects, UserEffects, CountriesEffects),
      provideTransloco({ config: translocoConfig(TRANSLOCO_CONFIG) }),
      {
        provide: APP_INITIALIZER,
        multi: true,
        deps: [TranslocoService],
        useFactory: loadTranslations,
      },
      { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
      },
      {
        provide: RECAPTCHA_SETTINGS,
        useValue: {
          siteKey: environment.recaptchaSiteKey,
        },
      },
    ],
    // eslint-disable-next-line no-console
  }).catch((err) => console.error(err));
}
